import * as THREE from 'three';
import type { Item, Level, Vec2, Wall } from '../../core/hp/types';
import { standsOn } from '../../core/geometry/stacking';
import { atticFrame, isAttic } from '../../state/attic';

/** Lite PlanSceneBuilder.cutWallHeight. Project coordinates put the floor at zero. */
export const LOW_WALL_HEIGHT = .35;
export interface SceneVisibilityMode {
  walk: boolean;
  /** Every wall cut to waist height — the native «низкие стены» view. */
  lowWalls: boolean;
  /** The orbit cutaway: only these walls are lowered, chosen for the camera as it stands. */
  cutWalls?: ReadonlySet<string>;
  /**
   * Where a wall is cut at this moment; null leaves it whole. `subject` says whether the
   * question is about the wall itself, which slides through every height in between, or
   * about what hangs in and on it — doors, windows, fixtures — which go straight to where
   * the wall is heading, so a slide re-cuts a few wall panels per frame and nothing else.
   * Absent means each lowered wall sits at LOW_WALL_HEIGHT.
   */
  cutHeight?: (wallId: string, subject: 'wall' | 'attachment') => number | null;
}

/** What one pass touches: the walls to re-cut, and the walls whose openings and fixtures follow. */
export interface VisibilityScope { walls: ReadonlySet<string>; attachments: ReadonlySet<string> }

interface CutState { original: THREE.BufferGeometry; cut: THREE.BufferGeometry; key: string }
const cuts = new WeakMap<THREE.Mesh, CutState>();

export function fullGeometryOf(mesh: THREE.Mesh): THREE.BufferGeometry { return cuts.get(mesh)?.original ?? mesh.geometry; }

/** Call before disposing a shell/instance so its original owned geometry is also released. */
export function restoreModeGeometry(root: THREE.Object3D): void {
  root.traverse(object => {
    if (!(object instanceof THREE.Mesh)) return;
    const state = cuts.get(object);
    if (state) { object.geometry = state.original; state.cut.dispose(); cuts.delete(object); }
  });
}

/** Clone the full model for the 2D pass, regardless of the current orbit cutaway. */
export function cloneFullModel(root: THREE.Object3D): THREE.Object3D {
  const copy = root.clone(true);
  const reset = (source: THREE.Object3D, target: THREE.Object3D) => {
    if (source instanceof THREE.Mesh && target instanceof THREE.Mesh) target.geometry = fullGeometryOf(source);
    if (source.userData.cohortHidden) { target.visible = true; delete target.userData.cohortHidden; }
    source.children.forEach((child, index) => reset(child, target.children[index]));
  };
  reset(root, copy); copy.visible = true;
  return copy;
}

function wallAttached(item: Item): boolean {
  // Native makeItem clips finish regions and thin elevated wall fixtures only.
  // The web IR additionally has an explicit wall placement, including wall cabinets.
  return item.placement === 'wall' || /^decor\.primitives\/1018\//.test(item.modelRef.id)
    || (item.elevation >= .5 && item.size.d <= .25 && item.placement !== 'ceiling');
}

const attachments = new WeakMap<Level['walls'], WeakMap<Item, string | null>>();
const supports = new WeakMap<Level['items'], Map<string, string | null>>();

/**
 * The wall an item goes down with. A fixture hangs on a wall directly: its own `wallId`
 * wins while it still stands against that wall, and a fixture moved elsewhere in the
 * editor keeps a stale id, so the nearest wall its back can touch is the fallback. A
 * vase on a wall shelf — or a cup on a tray on that shelf — belongs to the shelf's wall:
 * what stands on a fixture would float in the air once the fixture is gone. Null for
 * anything standing on the floor or on floor furniture.
 */
export function attachedWallId(item: Item, level: Level): string | null {
  // A mansard goes down with the wall whose face its back stands on.
  if (isAttic(item)) {
    let byItem = attachments.get(level.walls);
    if (!byItem) attachments.set(level.walls, byItem = new WeakMap());
    let id = byItem.get(item);
    if (id === undefined) byItem.set(item, id = atticFrame(item, level.walls).wall?.id ?? null);
    return id;
  }
  if (wallAttached(item)) {
    let byItem = attachments.get(level.walls);
    if (!byItem) attachments.set(level.walls, byItem = new WeakMap());
    let id = byItem.get(item);
    if (id === undefined) byItem.set(item, id = findAttachedWall(item, level.walls));
    return id;
  }
  if (item.placement !== 'floor' || item.elevation <= .04) return null;
  let byId = supports.get(level.items);
  if (!byId) supports.set(level.items, byId = new Map());
  let id = byId.get(item.id);
  if (id === undefined) byId.set(item.id, id = supportingWall(item, level, 0));
  return id;
}

/** The wall of whatever `item` stands on, through up to four layers of things on things. */
function supportingWall(item: Item, level: Level, depth: number): string | null {
  if (depth > 3) return null;
  for (const base of level.items) {
    if (!standsOn(item, base)) continue;
    const wall = wallAttached(base) ? attachedWallId(base, level) : supportingWall(base, level, depth + 1);
    if (wall) return wall;
  }
  return null;
}

function findAttachedWall(item: Item, walls: readonly Wall[]): string | null {
  const reach = item.size.d + .25;
  let best: Wall | null = null, bestDistance = Infinity, declaredFits = false;
  for (const wall of walls) {
    if (wall.hidden) continue;
    const dx = item.position.x - wall.start.x, dz = item.position.z - wall.start.z;
    const along = dx * wall.dir.x + dz * wall.dir.z;
    if (along < -.05 || along > wall.length + .05) continue;
    const across = Math.abs(dx * wall.normalIn.x + dz * wall.normalIn.z) - wall.thickness / 2;
    if (across > reach) continue;
    if (wall.id === item.wallId) declaredFits = true;
    if (across < bestDistance) { bestDistance = across; best = wall; }
  }
  if (declaredFits) return item.wallId!;
  return best?.id ?? item.wallId ?? null;
}

/** The camera on the plan: where it stands and which way it looks, both horizontal. */
export interface CutawayCamera { x: number; z: number; forward: Vec2 }

/**
 * sin 6° / sin 2°: a wall drops as soon as the view turns clearly off its plane — even a
 * wall seen at a slant hides the room behind it — and rises again only once the view
 * runs almost exactly along it, so a wall the camera skims does not flap while orbiting.
 */
const CUT_ON = .10, CUT_OFF = .035;

/**
 * The walls the orbit cutaway lowers for the camera — the Sims rule. A wall hides a room
 * from the camera when the camera stands on one side of it, looks through it, and indoor
 * floor lies beyond it along the view: an outside wall drops when the camera is outside
 * it and looking in, a partition between two rooms drops from either side, and a wall
 * seen from its inner face drops when the view crosses it into more of the house — the
 * far leg of an L-shaped room. A wall seen from inside with nothing but outdoors beyond
 * it stays up. Only rooms enclosed by proper walls count as indoors: a terrace, a balcony
 * or a garden slab with no walls or a parapet is open air, so the wall between it and the
 * living room keeps its picture. The side is judged from where the camera stands, the
 * facing from the direction it looks, so every wall along one front of the house drops
 * together instead of one by one as the camera passes. Walls the camera looks along drop
 * nothing worth seeing and stay up. `previous` carries the hysteresis; `out` is reused.
 */
export function cutawayWalls(level: Level, camera: CutawayCamera, previous?: ReadonlySet<string>, out: Set<string> = new Set()): Set<string> {
  out.clear();
  const facts = wallFacts(level);
  for (const wall of level.walls) {
    if (wall.hidden) continue;
    const inner = facts.inner.get(wall.id);
    if (!inner) continue;   // bounds no indoor room: a parapet, a garden wall, a loose stub
    const outside = (wall.center.x - camera.x) * inner.x + (wall.center.z - camera.z) * inner.z > 0;
    const facing = camera.forward.x * inner.x + camera.forward.z * inner.z;
    const threshold = previous?.has(wall.id) ? CUT_OFF : CUT_ON;
    const cut = outside ? facing > threshold
      : facing < -threshold && (facts.partition.has(wall.id) || roomAlongView(level, wall, camera.forward, facing, facts.indoor));
    if (cut) out.add(wall.id);
  }
  // A partition is two coincident walls, one per room: they drop together or not at all.
  for (const group of facts.twins) if (group.some(id => out.has(id))) for (const id of group) out.add(id);
  return out;
}

/**
 * Is there indoor floor along the view beyond this wall's far face? Sampled from three
 * points along the wall, at 0.1–1.5 m behind the far face, no further than four metres
 * along the view — about what a 2.7 m wall hides from an orbit camera at a 40° pitch. A
 * wall that stops the view short of any room hides nothing and can stay up.
 */
function roomAlongView(level: Level, wall: Wall, forward: Vec2, facing: number, indoor: ReadonlySet<number>): boolean {
  const face = wall.thickness / 2 + .05, slant = Math.abs(facing);
  for (const u of [.25, .5, .75]) {
    const bx = wall.start.x + wall.dir.x * wall.length * u, bz = wall.start.z + wall.dir.z * wall.length * u;
    for (const depth of [.1, .3, .7, 1.5]) {
      const along = (face + depth) / slant;
      if (along > 4) break;
      const room = roomAt(level.rooms, { x: bx + forward.x * along, z: bz + forward.z * along });
      if (room && indoor.has(room.index)) return true;
    }
  }
  return false;
}

/**
 * What the plan says about each wall, worked out from the room polygons rather than from the
 * wall's own fields. A drawn wall is turned to face its room once the room closes (`rebuildRooms`),
 * but a document saved before that kept the stroke's right-hand side as `normalIn` whichever way
 * the room lay, an import now and then lists a wall with its front towards the room next door, and
 * a wall detached from a shared boundary faces the room it left until its next edit — so the
 * polygons, not `normalIn`, decide which side of a wall is indoors.
 *
 *   inner      the unit normal towards the indoor room the wall bounds; absent when it bounds
 *              none (a parapet, a garden wall, a loose stub) — such a wall is never cut
 *   partition  indoor floor on both sides: a wall between two rooms, or a divider inside one
 *   twins      coincident collinear walls, one per room, that must drop together
 *   indoor     rooms with at least two proper walls; a terrace slab or a parapet yard is open air
 */
interface WallFacts { inner: Map<string, Vec2>; partition: Set<string>; twins: string[][]; indoor: Set<number> }

/** The tightest room around a point, so a nested room is not swallowed by the one enclosing it. */
function roomAt(rooms: readonly Level['rooms'][number][], point: Vec2): Level['rooms'][number] | undefined {
  let best: Level['rooms'][number] | undefined;
  for (const room of rooms) if (containsPoint(room.polygon, point) && (!best || room.area < best.area)) best = room;
  return best;
}
const factsCache = new WeakMap<Level['walls'], WeakMap<Level['rooms'], WallFacts>>();

function wallFacts(level: Level): WallFacts {
  let byRooms = factsCache.get(level.walls);
  if (!byRooms) factsCache.set(level.walls, byRooms = new WeakMap());
  let facts = byRooms.get(level.rooms);
  if (!facts) byRooms.set(level.rooms, facts = computeWallFacts(level));
  return facts;
}

function computeWallFacts(level: Level): WallFacts {
  const walls = level.walls.filter(wall => !wall.hidden);
  // The rooms on each side of a wall: probes a few centimetres beyond each face at three
  // points along it, against the tightest room polygon around each probe.
  const sides = new Map<string, [Set<number>, Set<number>]>();
  for (const wall of walls) {
    const beyond = wall.thickness / 2 + .06, found: [Set<number>, Set<number>] = [new Set(), new Set()];
    for (const u of [.25, .5, .75]) {
      const bx = wall.start.x + wall.dir.x * wall.length * u, bz = wall.start.z + wall.dir.z * wall.length * u;
      for (const [slot, sign] of [[0, 1], [1, -1]] as const) {
        const room = roomAt(level.rooms, { x: bx + wall.normalIn.x * beyond * sign, z: bz + wall.normalIn.z * beyond * sign });
        if (room) found[slot].add(room.index);
      }
    }
    sides.set(wall.id, found);
  }
  // A room is indoors when at least two proper walls stand on its edges; a floor slab with no
  // walls or a parapet is a terrace, and nothing behind it is worth a cut.
  const tall = new Map<number, Set<string>>();
  for (const wall of walls) {
    if (wall.height < 1.4) continue;
    for (const index of new Set([...sides.get(wall.id)![0], ...sides.get(wall.id)![1]])) {
      let owners = tall.get(index);
      if (!owners) tall.set(index, owners = new Set());
      owners.add(wall.id);
    }
  }
  const indoor = new Set(level.rooms.filter(room => (tall.get(room.index)?.size ?? 0) >= 2).map(room => room.index));
  const inner = new Map<string, Vec2>(), partition = new Set<string>();
  for (const wall of walls) {
    const [plus, minus] = sides.get(wall.id)!.map(set => [...set].some(index => indoor.has(index)));
    if (!plus && !minus) continue;
    inner.set(wall.id, plus ? wall.normalIn : { x: -wall.normalIn.x, z: -wall.normalIn.z });
    if (plus && minus) partition.add(wall.id);
  }
  const parent = new Map<string, string>();
  const find = (id: string): string => { const up = parent.get(id); if (!up || up === id) return id; const root = find(up); parent.set(id, root); return root; };
  for (let i = 0; i < walls.length; i++) for (let j = i + 1; j < walls.length; j++) {
    const a = walls[i], b = walls[j];
    if (Math.abs(a.dir.x * b.dir.x + a.dir.z * b.dir.z) < .999) continue;
    const dx = b.center.x - a.center.x, dz = b.center.z - a.center.z;
    if (Math.abs(dx * a.normalIn.x + dz * a.normalIn.z) > (a.thickness + b.thickness) / 2 + .03) continue;
    if (Math.abs(dx * a.dir.x + dz * a.dir.z) >= (a.length + b.length) / 2 - .1) continue;
    parent.set(find(a.id), find(b.id));
  }
  const groups = new Map<string, string[]>();
  for (const wall of walls) {
    const root = find(wall.id);
    if (root === wall.id && !parent.has(wall.id)) continue;
    let group = groups.get(root);
    if (!group) groups.set(root, group = []);
    group.push(wall.id);
  }
  return { inner, partition, twins: [...groups.values()].filter(group => group.length > 1), indoor };
}

function containsPoint(polygon: readonly Vec2[], point: Vec2): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[j], b = polygon[i];
    if ((a.z > point.z) !== (b.z > point.z) && point.x < (b.x - a.x) * (point.z - a.z) / (b.z - a.z) + a.x) inside = !inside;
  }
  return inside;
}

/**
 * Actual triangle cuts keep ray picking, shadow maps and GTAO consistent with the image.
 * `only` limits the pass: a set of walls means those walls with everything in and on them;
 * a scope separates the walls to re-cut from the walls whose openings and fixtures follow —
 * a sliding wall changes every frame, what hangs on it only when its destination does.
 */
export function applyModeVisibility(root: THREE.Object3D, level: Level, mode: SceneVisibilityMode,
  lookup: (name: string) => THREE.Object3D | undefined = name => root.getObjectByName(name),
  only?: ReadonlySet<string> | VisibilityScope): void {
  const scope: VisibilityScope | null = !only ? null : 'walls' in only ? only : { walls: only, attachments: only };
  const lowered = (wallId: string, subject: 'wall' | 'attachment'): number | null => {
    if (mode.walk) return null;
    if (mode.cutHeight) return mode.cutHeight(wallId, subject);
    return mode.lowWalls || mode.cutWalls?.has(wallId) ? LOW_WALL_HEIGHT : null;
  };
  const touchWall = (wallId: string) => !scope || scope.walls.has(wallId);
  const touchAttachment = (wallId: string) => !scope || scope.attachments.has(wallId);
  for (const wall of level.walls) {
    if (!touchWall(wall.id)) continue;
    const group = lookup(`wall:${wall.id}`);
    if (!group) continue;
    group.scale.y = 1; group.visible = !wall.hidden;
  }
  // A scoped pass refreshes only the matrices it reads; the whole tree is the full pass's job.
  if (!scope) root.updateMatrixWorld(true);
  const apply = (node: THREE.Object3D | undefined, height: number | null) => {
    if (!node) return;
    if (height === null) { restoreModeGeometry(node); return; }
    if (scope) node.updateWorldMatrix(true, true);
    node.traverse(object => {
      if (!(object instanceof THREE.Mesh)) return;
      const original = fullGeometryOf(object);
      const key = object.matrixWorld.elements.join(',') + ':' + height;
      if (cuts.get(object)?.key === key) return;
      const geometry = clippedBelow(original, object.matrixWorld, height, object.userData.kind === 'wall');
      const previous = cuts.get(object);
      previous?.cut.dispose();
      if (geometry === original) { object.geometry = original; cuts.delete(object); return; }
      cuts.set(object, { original, cut: geometry, key }); object.geometry = geometry;
    });
  };
  for (const wall of level.walls) if (touchWall(wall.id)) apply(lookup(`wall:${wall.id}`), lowered(wall.id, 'wall'));
  for (const opening of level.openings) {
    if (!touchAttachment(opening.wallId)) continue;
    const node = lookup(`opening:${opening.id}`);
    if (!node) continue;
    const height = lowered(opening.wallId, 'attachment');
    node.visible = height === null || opening.sill < height;
    apply(node, node.visible ? height : null);
  }
  for (const item of level.items) {
    const wallId = attachedWallId(item, level);
    if (scope && !(wallId && scope.attachments.has(wallId))) continue;
    const node = lookup(`item:${item.id}`);
    if (!node) continue;
    // A fixture the walls cannot account for follows the whole-scene cut, as it always did.
    const height = wallId ? lowered(wallId, 'attachment') : wallAttached(item) && mode.lowWalls && !mode.walk ? LOW_WALL_HEIGHT : null;
    // Ceiling fixtures show only from inside, as the app hides a ceiling's furniture with the ceiling
    // (CeilingFurnitureController.IsShownIn3D); a mansard is the room's shape and shows from above too.
    node.visible = (mode.walk || item.placement !== 'ceiling' || isAttic(item)) && (height === null || item.elevation < height);
    apply(node, node.visible ? height : null);
  }
}

const KEEP = -1e-7;

/**
 * Native ObjModelLoader.clipped: keep the triangles below a horizontal cut, splitting the
 * ones it crosses at the plane with every attribute interpolated. The result is indexed
 * and reuses the source's vertex data — the work per triangle is three distance lookups,
 * and only the handful of edges the plane crosses make new vertices — so a door or a
 * cabinet of a hundred thousand triangles is cut in a few milliseconds without a single
 * per-vertex allocation. Wall panels get a real cap over the cut (material slot 2).
 */
export function clippedBelow(source: THREE.BufferGeometry, matrix: THREE.Matrix4, height: number, cap = false): THREE.BufferGeometry {
  const plane = new THREE.Plane(new THREE.Vector3(0, -1, 0), height).applyMatrix4(matrix.clone().invert());
  const nx = plane.normal.x, ny = plane.normal.y, nz = plane.normal.z, offset = plane.constant;
  const attributes = Object.entries(source.attributes);
  if (!source.boundingBox) source.computeBoundingBox();
  const box = source.boundingBox!;
  let low = Infinity, high = -Infinity;
  for (let i = 0; i < 8; i++) {
    const distance = nx * (i & 1 ? box.max.x : box.min.x) + ny * (i & 2 ? box.max.y : box.min.y) + nz * (i & 4 ? box.max.z : box.min.z) + offset;
    low = Math.min(low, distance); high = Math.max(high, distance);
  }
  if (low >= KEEP) return source;
  if (high < KEEP || box.isEmpty()) {
    const empty = new THREE.BufferGeometry();
    for (const [name, attribute] of attributes) empty.setAttribute(name, new THREE.Float32BufferAttribute([], attribute.itemSize));
    empty.computeBoundingBox(); empty.computeBoundingSphere();
    return empty;
  }
  const position = source.getAttribute('position'), count = position.count;
  const distance = new Float32Array(count);
  for (let i = 0; i < count; i++) distance[i] = nx * position.getX(i) + ny * position.getY(i) + nz * position.getZ(i) + offset;
  const index = source.getIndex();
  const indexCount = index ? index.count : count;
  const vertexOf = index ? (k: number) => index.getX(k) : (k: number) => k;
  const positionSlot = attributes.findIndex(([name]) => name === 'position');
  const normalSlot = attributes.findIndex(([name]) => name === 'normal');
  const extra: number[][] = attributes.map(() => []);
  let added = 0;
  const crossings = new Map<number, number>();
  const intersections: THREE.Vector3[] = [];
  /** The vertex where edge a–b meets the cut, made once per edge however many triangles share it. */
  const crossing = (a: number, b: number): number => {
    const key = a < b ? a * count + b : b * count + a;
    const known = crossings.get(key);
    if (known !== undefined) return known;
    const t = distance[a] / (distance[a] - distance[b]);
    for (let slot = 0; slot < attributes.length; slot++) {
      const attribute = attributes[slot][1], out = extra[slot], start = out.length;
      for (let c = 0; c < attribute.itemSize; c++) {
        const from = attribute.getComponent(a, c);
        out.push(from + (attribute.getComponent(b, c) - from) * t);
      }
      if (slot === normalSlot) {
        const length = Math.hypot(out[start], out[start + 1], out[start + 2]) || 1;
        out[start] /= length; out[start + 1] /= length; out[start + 2] /= length;
      }
    }
    if (cap) { const p = extra[positionSlot]; intersections.push(new THREE.Vector3(p[p.length - 3], p[p.length - 2], p[p.length - 1])); }
    const id = count + added++;
    crossings.set(key, id);
    return id;
  };
  const outIndex: number[] = [];
  const groups = source.groups.length ? source.groups : [{ start: 0, count: indexCount, materialIndex: 0 }];
  const outGroups: Array<{ start: number; count: number; materialIndex: number }> = [];
  const corners = [0, 0, 0], polygon: number[] = [];
  for (const group of groups) {
    const start = outIndex.length, end = Math.min(group.start + group.count, indexCount);
    for (let k = group.start; k + 2 < end; k += 3) {
      const a = vertexOf(k), b = vertexOf(k + 1), c = vertexOf(k + 2);
      const keepA = distance[a] >= KEEP, keepB = distance[b] >= KEEP, keepC = distance[c] >= KEEP;
      if (keepA && keepB && keepC) { outIndex.push(a, b, c); continue; }
      if (!keepA && !keepB && !keepC) continue;
      corners[0] = a; corners[1] = b; corners[2] = c; polygon.length = 0;
      for (let edge = 0; edge < 3; edge++) {
        const from = corners[edge], to = corners[(edge + 1) % 3];
        const keepFrom = distance[from] >= KEEP, keepTo = distance[to] >= KEEP;
        if (keepFrom) polygon.push(from);
        if (keepFrom !== keepTo) polygon.push(crossing(from, to));
      }
      for (let m = 1; m + 1 < polygon.length; m++) outIndex.push(polygon[0], polygon[m], polygon[m + 1]);
    }
    outGroups.push({ start, count: outIndex.length - start, materialIndex: group.materialIndex ?? 0 });
  }
  if (cap && intersections.length >= 3) {
    // Wall panels are convex prisms. Sorted unique edge crossings form their exact
    // horizontal cap; imported furniture intentionally stays uncapped, like Lite.
    const unique = [...new Map(intersections.map(p => [p.toArray().map(v => v.toFixed(6)).join(','), p])).values()];
    const world = unique.map(p => p.clone().applyMatrix4(matrix));
    const centre = world.reduce((a, b) => a.add(b), new THREE.Vector3()).multiplyScalar(1 / world.length);
    const order = world.map((p, i) => ({ i, angle: Math.atan2(p.z - centre.z, p.x - centre.x) })).sort((a, b) => b.angle - a.angle);
    const points = order.map(({ i }) => unique[i]);
    const normal = new THREE.Vector3(0, 1, 0).transformDirection(matrix.clone().transpose());
    const start = outIndex.length, first = count + added;
    for (const p of points) {
      for (let slot = 0; slot < attributes.length; slot++) {
        const [name, attribute] = attributes[slot];
        const values = name === 'position' ? [p.x, p.y, p.z] : name === 'normal' ? [normal.x, normal.y, normal.z]
          : name === 'uv' ? [p.x, p.z] : new Array<number>(attribute.itemSize).fill(0);
        extra[slot].push(...values);
      }
      added++;
    }
    for (let k = 1; k + 1 < points.length; k++) outIndex.push(first, first + k, first + k + 1);
    outGroups.push({ start, count: outIndex.length - start, materialIndex: 2 });
  }
  // Only the vertices the remaining triangles use travel, in first-use order, so the
  // bounding volumes are those of what is drawn and nothing above the cut lingers.
  const remap = new Int32Array(count + added).fill(-1);
  let used = 0;
  for (let i = 0; i < outIndex.length; i++) {
    const vertex = outIndex[i];
    if (remap[vertex] < 0) remap[vertex] = used++;
    outIndex[i] = remap[vertex];
  }
  const result = new THREE.BufferGeometry();
  for (let slot = 0; slot < attributes.length; slot++) {
    const [name, attribute] = attributes[slot], size = attribute.itemSize;
    const array = new Float32Array(used * size), fresh = extra[slot];
    const raw = (attribute as THREE.BufferAttribute).array;
    const direct = !(attribute as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute && raw instanceof Float32Array && !attribute.normalized;
    for (let vertex = 0; vertex < count; vertex++) {
      const target = remap[vertex];
      if (target < 0) continue;
      for (let c = 0; c < size; c++) array[target * size + c] = direct ? raw[vertex * size + c] : attribute.getComponent(vertex, c);
    }
    for (let vertex = 0; vertex < added; vertex++) {
      const target = remap[count + vertex];
      if (target < 0) continue;
      for (let c = 0; c < size; c++) array[target * size + c] = fresh[vertex * size + c];
    }
    result.setAttribute(name, new THREE.BufferAttribute(array, size));
  }
  result.setIndex(outIndex);
  for (const group of outGroups) result.addGroup(group.start, group.count, group.materialIndex);
  result.computeBoundingBox(); result.computeBoundingSphere();
  return result;
}
